import React, { useId } from 'react';

export interface EnclosureDoorSvgProps {
  /** Kapak açık mı (menteşeli yana açılmış kablolama modu) yoksa kapalı mı (operatör ön yüzü)? */
  isOpen: boolean;
  hasSb1?: boolean;
  hasSb2?: boolean;
  hasHl1?: boolean;
  hasHl2?: boolean;
  selectedPaletteItemId?: string | null;
  onPlaceDevice?: (deviceId: string) => void;
  stopPressed?: boolean;
  startPressed?: boolean;
  lampRunLit?: boolean;
  lampFaultLit?: boolean;
  onStopDown?: () => void;
  onStopUp?: () => void;
  onStartDown?: () => void;
  onStartUp?: () => void;
  terminalValues?: Record<string, string>;
  activeTerminalId?: string | null | undefined;
  onTerminalClick?: (terminalId: string, e: React.MouseEvent) => void;
  onToggleDoor?: () => void;
}

/**
 * EnclosureDoorSvg
 * 400x600 mm Endüstriyel Pano Kapağı (Menteşeli Yana Açılan & Kapanan Kapak)
 * - Açık Durumda (isOpen=true):
 *   Kapak sağa doğru menteşeyle açılır; kapak arka yüzündeki 22mm Start/Stop kontak blokları
 *   ve pilot lamba duyları (1-2 NC, 3-4 NO, X1-X2) vidalı klemensleriyle görünür ve kablolanabilir.
 * - Kapalı Durumda (isOpen=false):
 *   Kapak panonun üzerini örter; şık endüstriyel operatör ön yüzü, çeyrek dönüşlü kilitler,
 *   ergonomik kulp, 22mm yaylı Start/Stop butonları ve yanan optik Fresnel lensli lambalar görünür.
 */
export const EnclosureDoorSvg: React.FC<EnclosureDoorSvgProps> = ({
  isOpen,
  hasSb1 = true,
  hasSb2 = true,
  hasHl1 = true,
  hasHl2 = true,
  selectedPaletteItemId = null,
  onPlaceDevice,
  stopPressed = false,
  startPressed = false,
  lampRunLit = false,
  lampFaultLit = false,
  onStopDown,
  onStopUp,
  onStartDown,
  onStartUp,
  terminalValues = {},
  activeTerminalId = null,
  onTerminalClick,
  onToggleDoor,
}) => {
  const rawId = useId();
  const uid = rawId.replace(/[:]/g, '_');

  const getTerminalColor = (tid: string): string => {
    const pot = terminalValues[tid];
    if (pot === 'L1') return '#f59e0b';
    if (pot === 'L2') return '#ea580c';
    if (pot === 'L3') return '#cbd5e1';
    if (pot === 'N') return '#38bdf8';
    if (pot === 'PE') return '#84cc16';
    return '#94a3b8';
  };

  const renderScrewTerminal = (
    tid: string,
    label: string,
    cx: number,
    cy: number,
    labelPos: 'left' | 'right' = 'left'
  ) => {
    const isActive = activeTerminalId === tid;
    const termColor = getTerminalColor(tid);
    const hasPotential = terminalValues[tid] && terminalValues[tid] !== 'FLOATING' && terminalValues[tid] !== '0V';

    return (
      <g
        key={tid}
        transform={`translate(${cx}, ${cy})`}
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onTerminalClick?.(tid, e);
        }}
      >
        <title>{`${label} Klemensi (${tid}) — ${terminalValues[tid] || '0V'}`}</title>
        {/* Dış terminal kovanı */}
        <circle cx="0" cy="0" r="7" fill="#1e293b" stroke="#475569" strokeWidth="1.2" />
        {/* Pirinç/metal vida başı */}
        <circle cx="0" cy="0" r="4.5" fill="#94a3b8" stroke="#334155" strokeWidth="0.8" />
        <line x1="-3" y1="-1" x2="3" y2="1" stroke="#1e293b" strokeWidth="1.4" strokeLinecap="round" />

        {/* Canlı potansiyel halkası */}
        {hasPotential && (
          <circle cx="0" cy="0" r="8" fill="none" stroke={termColor} strokeWidth="1.5" strokeOpacity="0.9" />
        )}

        {/* Aktif seçim vurgusu */}
        {isActive && (
          <circle cx="0" cy="0" r="10" fill="none" stroke="#38bdf8" strokeWidth="2" strokeDasharray="3,2" />
        )}

        <text
          x={labelPos === 'left' ? -11 : 11}
          y="3.5"
          fill="#f8fafc"
          fontSize="7.5"
          fontFamily="system-ui, monospace"
          fontWeight="bold"
          textAnchor={labelPos === 'left' ? 'end' : 'start'}
        >
          {label}
        </text>
      </g>
    );
  };

  return (
    <g id="enclosure-door-system">
      <defs>
        {/* Kapak Dış Gövde Degradesi */}
        <linearGradient id={`doorFrontGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="25%" stopColor="#e2e8f0" />
          <stop offset="70%" stopColor="#cbd5e1" />
          <stop offset="100%" stopColor="#94a3b8" />
        </linearGradient>

        {/* Kapak Arka İç Yüz Degradesi */}
        <linearGradient id={`doorBackGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#475569" />
          <stop offset="50%" stopColor="#334155" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Çelik Menteşe Degradesi */}
        <linearGradient id={`steelHingeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#334155" />
          <stop offset="20%" stopColor="#94a3b8" />
          <stop offset="50%" stopColor="#f8fafc" />
          <stop offset="80%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Kapak Gölgesi */}
        <filter id={`doorShadow-${uid}`} x="-10%" y="-10%" width="130%" height="130%">
          <feDropShadow dx="8" dy="12" stdDeviation="10" floodColor="#050811" floodOpacity="0.55" />
          <feDropShadow dx="2" dy="4" stdDeviation="4" floodColor="#050811" floodOpacity="0.35" />
        </filter>

        {/* Yeşil RUN Lambası Glow Filtresi */}
        <filter id={`greenGlow-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Kırmızı FAULT Lambası Glow Filtresi */}
        <filter id={`redGlow-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ============================================================ */}
      {/* DURUM 1: KAPAK AÇIK (MENTEŞELİ SAĞDA, KABLOLAMA ARKA YÜZÜ)   */}
      {/* ============================================================ */}
      {isOpen && (
        <g id="hinged-door-open" transform="translate(650, 20)">
          {/* 1. Çelik Menteşeler (Pano ile Kapağı Bağlayan Üst ve Alt Menteşe) */}
          <g id="door-hinges" transform="translate(-30, 0)">
            {/* Üst Menteşe (Y = 120) */}
            <g transform="translate(0, 110)">
              <rect x="-8" y="0" width="36" height="24" rx="3" fill={`url(#steelHingeGrad-${uid})`} stroke="#1e293b" strokeWidth="1.2" />
              <circle cx="10" cy="12" r="5" fill="#475569" stroke="#0f172a" strokeWidth="1" />
              <circle cx="10" cy="12" r="2.5" fill="#94a3b8" />
              <line x1="-3" y1="5" x2="-3" y2="19" stroke="#1e293b" strokeWidth="1.5" />
              <line x1="23" y1="5" x2="23" y2="19" stroke="#1e293b" strokeWidth="1.5" />
            </g>

            {/* Alt Menteşe (Y = 660) */}
            <g transform="translate(0, 650)">
              <rect x="-8" y="0" width="36" height="24" rx="3" fill={`url(#steelHingeGrad-${uid})`} stroke="#1e293b" strokeWidth="1.2" />
              <circle cx="10" cy="12" r="5" fill="#475569" stroke="#0f172a" strokeWidth="1" />
              <circle cx="10" cy="12" r="2.5" fill="#94a3b8" />
              <line x1="-3" y1="5" x2="-3" y2="19" stroke="#1e293b" strokeWidth="1.5" />
              <line x1="23" y1="5" x2="23" y2="19" stroke="#1e293b" strokeWidth="1.5" />
            </g>
          </g>

          {/* 2. Esnek Spiral Kablo Koruma Hortumu (Door Wiring Loom) */}
          <g id="spiral-wiring-loom" opacity="0.85">
            <path
              d="M -30 330 C -10 330, 0 350, 40 350"
              fill="none"
              stroke="#0f172a"
              strokeWidth="20"
              strokeLinecap="round"
            />
            <path
              d="M -30 330 C -10 330, 0 350, 40 350"
              fill="none"
              stroke="#1e293b"
              strokeWidth="16"
              strokeLinecap="round"
              strokeDasharray="6,4"
            />
            {/* Spiral kelepçe ucu */}
            <rect x="36" y="338" width="10" height="24" rx="2" fill="#475569" stroke="#0f172a" strokeWidth="1" />
          </g>

          {/* 3. Açık Kapak Gövdesi (Arka Sac / Arka Yüz: 600 x 800 mm Tam Boyut) */}
          <g id="door-panel-body" filter={`url(#doorShadow-${uid})`}>
            {/* Kapak Dış Çerçevesi (400 x 600 mm Gerçek Endüstriyel Kapı Arka Yüzü) */}
            <rect
              x="0"
              y="0"
              width="600"
              height="800"
              rx="26"
              ry="26"
              fill={`url(#doorBackGrad-${uid})`}
              stroke="#64748b"
              strokeWidth="2"
            />

            {/* Poliüretan IP65 Sıvı Dökme Conta (Perimeter Foam-in-Place Seal) */}
            <rect
              x="12"
              y="12"
              width="576"
              height="776"
              rx="18"
              fill="none"
              stroke="#0f172a"
              strokeWidth="6"
            />
            <rect
              x="16"
              y="16"
              width="568"
              height="768"
              rx="16"
              fill="none"
              stroke="#334155"
              strokeWidth="1.2"
              strokeDasharray="4,4"
            />

            {/* Kapı Takviye Profilleri (Galvanizli Perfore Takviye Kanalları) */}
            <g id="door-stiffener-rails" opacity="0.6">
              {/* Sol Takviye Profili (Menteşe Tarafı) */}
              <rect x="40" y="80" width="16" height="680" rx="3" fill="#1e293b" stroke="#475569" strokeWidth="1" />
              {[120, 180, 240, 300, 360, 420, 480, 540, 600, 660, 720].map((py) => (
                <rect key={`stiff-l-${py}`} x="44" y={py} width="8" height="14" rx="2" fill="#0f172a" />
              ))}

              {/* Sağ Takviye Profili (Kilit Tarafı) */}
              <rect x="544" y="80" width="16" height="680" rx="3" fill="#1e293b" stroke="#475569" strokeWidth="1" />
              {[120, 180, 240, 300, 360, 420, 480, 540, 600, 660, 720].map((py) => (
                <rect key={`stiff-r-${py}`} x="548" y={py} width="8" height="14" rx="2" fill="#0f172a" />
              ))}
            </g>

            {/* Sağ Kenar İç Kilit Mandalları (Açıkken Görünen Çift Kilit Dilleri) */}
            <g id="door-inside-locks" opacity="0.9">
              {/* Üst Kilit Dili (Y = 120) */}
              <g transform="translate(565, 120)">
                <circle cx="0" cy="0" r="12" fill="#334155" stroke="#1e293b" strokeWidth="1.5" />
                <rect x="-4" y="-8" width="18" height="16" rx="2" fill="#94a3b8" stroke="#475569" strokeWidth="1" />
                <circle cx="0" cy="0" r="4" fill="#0f172a" />
              </g>
              {/* Alt Kilit Dili (Y = 660) */}
              <g transform="translate(565, 660)">
                <circle cx="0" cy="0" r="12" fill="#334155" stroke="#1e293b" strokeWidth="1.5" />
                <rect x="-4" y="-8" width="18" height="16" rx="2" fill="#94a3b8" stroke="#475569" strokeWidth="1" />
                <circle cx="0" cy="0" r="4" fill="#0f172a" />
              </g>
              {/* Gömme Kulp Arka Haznesi (Y = 360) */}
              <g transform="translate(552, 360)">
                <rect x="0" y="0" width="20" height="70" rx="4" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5" />
                <rect x="3" y="6" width="14" height="58" rx="2" fill="#0f172a" />
              </g>
            </g>

            {/* Kapak Üst Başlığı & Durum Butonu (Genişlik: 540mm) */}
            <g transform="translate(30, 24)">
              <rect x="0" y="0" width="540" height="44" rx="6" fill="#1e293b" stroke="#334155" strokeWidth="1.2" />
              <text x="18" y="20" fill="#38bdf8" fontSize="12" fontWeight="bold">
                KUMANDA KUTUSU
              </text>
              <text x="138" y="20" fill="#94a3b8" fontSize="9.5">
                — KAPI İÇ GÖRÜNÜŞÜ (KABLOLAMA MODU)
              </text>
              <text x="18" y="34" fill="#64748b" fontSize="8">
                400 × 600 mm Endüstriyel Pano Kapağı (Arka Yüz / Klemens Bağlantı Alanı)
              </text>

              {/* Kapağı Kapat Butonu */}
              <g
                transform="translate(420, 8)"
                style={{ cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleDoor?.();
                }}
              >
                <rect x="0" y="0" width="108" height="28" rx="5" fill="#0284c7" stroke="#38bdf8" strokeWidth="1" />
                <text x="54" y="18" fill="#ffffff" fontSize="10.5" fontWeight="bold" textAnchor="middle">
                  🚪 Kapağı Kapat
                </text>
              </g>
            </g>

            {/* 4. Kapak Üzerindeki 22mm Kumanda Elemanlarının Arka Kontak Blokları (2x2 Grid) */}
            <g id="door-rear-components">
              {/* ============================================================ */}
              {/* SATIR 1: SİNYAL LAMBALARI (HL2 Solda X=200, HL1 Sağda X=400) */}
              {/* ============================================================ */}

              {/* --- ELEMAN: HL2 ARIZA SİNYAL LAMBASI (22mm Kırmızı LED Duyu - X1/X2) --- */}
              {hasHl2 ? (
                <g id="rear-hl2" transform="translate(115, 235)">
                  <rect x="0" y="0" width="170" height="90" rx="6" fill="#1e293b" stroke="#dc2626" strokeWidth="1.5" />
                  <rect x="2" y="2" width="166" height="18" rx="4" fill="#7f1d1d" />
                  <text x="10" y="14" fill="#fecaca" fontSize="8" fontWeight="bold">
                    HL2 — ARIZA LAMBASI (22mm KIRMIZI LED)
                  </text>
                  <text x="135" y="14" fill="#f87171" fontSize="8" fontWeight="bold">X1 - X2</text>

                  <rect x="50" y="26" width="70" height="48" rx="3" fill="#991b1b" stroke="#dc2626" strokeWidth="1" />
                  <circle cx="85" cy="50" r="14" fill={lampFaultLit ? '#ef4444' : '#7f1d1d'} stroke="#f87171" strokeWidth="1.2" />
                  <text x="85" y="54" fill="#ffffff" fontSize="9" fontWeight="bold" textAnchor="middle">
                    {lampFaultLit ? 'ARIZA' : 'HL2'}
                  </text>

                  {/* Klemens X1 (Faz Girişi) ve X2 (Nötr Dönüşü) */}
                  {renderScrewTerminal('hl2:X1', 'X1', 25, 50, 'left')}
                  {renderScrewTerminal('hl2:X2', 'X2', 145, 50, 'right')}
                </g>
              ) : (
                <g
                  id="slot-hl2"
                  transform="translate(115, 235)"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceDevice?.('hl2');
                  }}
                >
                  <rect
                    x="0"
                    y="0"
                    width="170"
                    height="90"
                    rx="6"
                    fill={selectedPaletteItemId === 'hl2' ? 'rgba(239, 68, 68, 0.18)' : 'rgba(30, 41, 59, 0.4)'}
                    stroke={selectedPaletteItemId === 'hl2' ? '#ef4444' : '#475569'}
                    strokeWidth={selectedPaletteItemId === 'hl2' ? 2 : 1}
                    strokeDasharray="4,4"
                  />
                  <circle cx="85" cy="40" r="16" fill="#0f172a" stroke="#ef4444" strokeWidth="1" strokeDasharray="3,3" />
                  <text x="85" y="44" fill={selectedPaletteItemId === 'hl2' ? '#f87171' : '#94a3b8'} fontSize="8" fontWeight="bold" textAnchor="middle">
                    {selectedPaletteItemId === 'hl2' ? '👇 HL2 Monte Et' : '+ HL2 Arıza Lambası'}
                  </text>
                  <text x="85" y="72" fill="#64748b" fontSize="7" textAnchor="middle">Ø22mm Arka Klemens Yuvası</text>
                </g>
              )}

              {/* --- ELEMAN: HL1 ÇALIŞMA SİNYAL LAMBASI (22mm Yeşil LED Duyu - X1/X2) --- */}
              {hasHl1 ? (
                <g id="rear-hl1" transform="translate(315, 235)">
                  <rect x="0" y="0" width="170" height="90" rx="6" fill="#1e293b" stroke="#16a34a" strokeWidth="1.5" />
                  <rect x="2" y="2" width="166" height="18" rx="4" fill="#064e3b" />
                  <text x="10" y="14" fill="#a7f3d0" fontSize="8" fontWeight="bold">
                    HL1 — ÇALIŞMA LAMBASI (22mm YEŞİL LED)
                  </text>
                  <text x="135" y="14" fill="#34d399" fontSize="8" fontWeight="bold">X1 - X2</text>

                  <rect x="50" y="26" width="70" height="48" rx="3" fill="#047857" stroke="#10b981" strokeWidth="1" />
                  <circle cx="85" cy="50" r="14" fill={lampRunLit ? '#22c55e' : '#064e3b'} stroke="#34d399" strokeWidth="1.2" />
                  <text x="85" y="54" fill="#ffffff" fontSize="9" fontWeight="bold" textAnchor="middle">
                    {lampRunLit ? 'AÇIK' : 'HL1'}
                  </text>

                  {/* Klemens X1 (Faz Girişi) ve X2 (Nötr Dönüşü) */}
                  {renderScrewTerminal('hl1:X1', 'X1', 25, 50, 'left')}
                  {renderScrewTerminal('hl1:X2', 'X2', 145, 50, 'right')}
                </g>
              ) : (
                <g
                  id="slot-hl1"
                  transform="translate(315, 235)"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceDevice?.('hl1');
                  }}
                >
                  <rect
                    x="0"
                    y="0"
                    width="170"
                    height="90"
                    rx="6"
                    fill={selectedPaletteItemId === 'hl1' ? 'rgba(34, 197, 94, 0.18)' : 'rgba(30, 41, 59, 0.4)'}
                    stroke={selectedPaletteItemId === 'hl1' ? '#22c55e' : '#475569'}
                    strokeWidth={selectedPaletteItemId === 'hl1' ? 2 : 1}
                    strokeDasharray="4,4"
                  />
                  <circle cx="85" cy="40" r="16" fill="#0f172a" stroke="#22c55e" strokeWidth="1" strokeDasharray="3,3" />
                  <text x="85" y="44" fill={selectedPaletteItemId === 'hl1' ? '#4ade80' : '#94a3b8'} fontSize="8" fontWeight="bold" textAnchor="middle">
                    {selectedPaletteItemId === 'hl1' ? '👇 HL1 Monte Et' : '+ HL1 Çalışma Lambası'}
                  </text>
                  <text x="85" y="72" fill="#64748b" fontSize="7" textAnchor="middle">Ø22mm Arka Klemens Yuvası</text>
                </g>
              )}

              {/* ============================================================ */}
              {/* SATIR 2: KUMANDA BUTONLARI (SB2 Solda X=200, SB1 Sağda X=400) */}
              {/* ============================================================ */}

              {/* --- ELEMAN: SB2 START BUTONU (22mm Yeşil Kontak Bloğu - 3/4 NO) --- */}
              {hasSb2 ? (
                <g id="rear-sb2" transform="translate(115, 435)">
                  <rect x="0" y="0" width="170" height="90" rx="6" fill="#1e293b" stroke="#22c55e" strokeWidth="1.5" />
                  <rect x="2" y="2" width="166" height="18" rx="4" fill="#14532d" />
                  <text x="10" y="14" fill="#bbf7d0" fontSize="8" fontWeight="bold">
                    SB2 — START BUTONU (22mm NO KONTAK)
                  </text>
                  <text x="135" y="14" fill="#4ade80" fontSize="8" fontWeight="bold">3-4 NO</text>

                  <rect x="50" y="26" width="70" height="48" rx="3" fill="#166534" stroke="#22c55e" strokeWidth="1" />
                  <circle cx="85" cy="50" r="14" fill="#14532d" stroke="#4ade80" strokeWidth="1" />
                  <text x="85" y="54" fill="#ffffff" fontSize="10" fontWeight="bold" textAnchor="middle">START</text>

                  {/* Klemens 3 (Sol) ve Klemens 4 (Sağ) */}
                  {renderScrewTerminal('sb2:3', '3 (NO)', 25, 50, 'left')}
                  {renderScrewTerminal('sb2:4', '4 (NO)', 145, 50, 'right')}
                </g>
              ) : (
                <g
                  id="slot-sb2"
                  transform="translate(115, 435)"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceDevice?.('sb2');
                  }}
                >
                  <rect
                    x="0"
                    y="0"
                    width="170"
                    height="90"
                    rx="6"
                    fill={selectedPaletteItemId === 'sb2' ? 'rgba(34, 197, 94, 0.18)' : 'rgba(30, 41, 59, 0.4)'}
                    stroke={selectedPaletteItemId === 'sb2' ? '#22c55e' : '#475569'}
                    strokeWidth={selectedPaletteItemId === 'sb2' ? 2 : 1}
                    strokeDasharray="4,4"
                  />
                  <circle cx="85" cy="40" r="16" fill="#0f172a" stroke="#22c55e" strokeWidth="1" strokeDasharray="3,3" />
                  <text x="85" y="44" fill={selectedPaletteItemId === 'sb2' ? '#4ade80' : '#94a3b8'} fontSize="8" fontWeight="bold" textAnchor="middle">
                    {selectedPaletteItemId === 'sb2' ? '👇 SB2 START Monte Et' : '+ SB2 START Buton Yuvası'}
                  </text>
                  <text x="85" y="72" fill="#64748b" fontSize="7" textAnchor="middle">Ø22mm NO Kontak Bloğu</text>
                </g>
              )}

              {/* --- ELEMAN: SB1 STOP BUTONU (22mm Kırmızı Kontak Bloğu - 1/2 NC) --- */}
              {hasSb1 ? (
                <g id="rear-sb1" transform="translate(315, 435)">
                  <rect x="0" y="0" width="170" height="90" rx="6" fill="#1e293b" stroke="#ef4444" strokeWidth="1.5" />
                  <rect x="2" y="2" width="166" height="18" rx="4" fill="#7f1d1d" />
                  <text x="10" y="14" fill="#fecaca" fontSize="8" fontWeight="bold">
                    SB1 — STOP BUTONU (22mm NC KONTAK)
                  </text>
                  <text x="135" y="14" fill="#f87171" fontSize="8" fontWeight="bold">1-2 NC</text>

                  <rect x="50" y="26" width="70" height="48" rx="3" fill="#991b1b" stroke="#ef4444" strokeWidth="1" />
                  <circle cx="85" cy="50" r="14" fill="#7f1d1d" stroke="#f87171" strokeWidth="1" />
                  <text x="85" y="54" fill="#ffffff" fontSize="10" fontWeight="bold" textAnchor="middle">STOP</text>

                  {/* Klemens 1 (Sol) ve Klemens 2 (Sağ) */}
                  {renderScrewTerminal('sb1:1', '1 (NC)', 25, 50, 'left')}
                  {renderScrewTerminal('sb1:2', '2 (NC)', 145, 50, 'right')}
                </g>
              ) : (
                <g
                  id="slot-sb1"
                  transform="translate(315, 435)"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceDevice?.('sb1');
                  }}
                >
                  <rect
                    x="0"
                    y="0"
                    width="170"
                    height="90"
                    rx="6"
                    fill={selectedPaletteItemId === 'sb1' ? 'rgba(239, 68, 68, 0.18)' : 'rgba(30, 41, 59, 0.4)'}
                    stroke={selectedPaletteItemId === 'sb1' ? '#ef4444' : '#475569'}
                    strokeWidth={selectedPaletteItemId === 'sb1' ? 2 : 1}
                    strokeDasharray="4,4"
                  />
                  <circle cx="85" cy="40" r="16" fill="#0f172a" stroke="#ef4444" strokeWidth="1" strokeDasharray="3,3" />
                  <text x="85" y="44" fill={selectedPaletteItemId === 'sb1' ? '#f87171' : '#94a3b8'} fontSize="8" fontWeight="bold" textAnchor="middle">
                    {selectedPaletteItemId === 'sb1' ? '👇 SB1 STOP Monte Et' : '+ SB1 STOP Buton Yuvası'}
                  </text>
                  <text x="85" y="72" fill="#64748b" fontSize="7" textAnchor="middle">Ø22mm NC Kontak Bloğu</text>
                </g>
              )}
            </g>

            {/* 5. Alt Elektrik Proje & Şema Cebi (IEC 60204-1 Standardı Pano Kapağı Cebi) */}
            <g id="door-drawing-pocket" transform="translate(140, 595)">
              <rect x="0" y="0" width="320" height="120" rx="8" fill="#1e293b" stroke="#334155" strokeWidth="1.5" />
              <rect x="4" y="4" width="312" height="112" rx="6" fill="#0f172a" opacity="0.6" />
              {/* Katlanmış Proje Kağıdı Detayı */}
              <path d="M 30 18 L 290 18 L 290 40 L 30 40 Z" fill="#e2e8f0" opacity="0.8" />
              <line x1="40" y1="26" x2="280" y2="26" stroke="#94a3b8" strokeWidth="1" />
              <line x1="40" y1="32" x2="220" y2="32" stroke="#cbd5e1" strokeWidth="1" />
              <rect x="15" y="40" width="290" height="70" rx="4" fill="#1e293b" stroke="#475569" strokeWidth="1" />
              <text x="160" y="65" fill="#38bdf8" fontSize="10" fontWeight="bold" textAnchor="middle">
                📄 ELEKTRİK PROJE VE DEVRE ŞEMASI CEBİ
              </text>
              <text x="160" y="80" fill="#94a3b8" fontSize="8" textAnchor="middle">
                IEC 60204-1 Madde 17 — Dokümantasyon Muhafazası
              </text>
              <text x="160" y="94" fill="#64748b" fontSize="7.5" textAnchor="middle">
                Kumanda ve güç devre bağlantı şemaları burada saklanır.
              </text>
            </g>

            {/* 6. Kapak Gövde Topraklama Pabucu (M6 Pirinç Civata + Sarı/Yeşil PE Etiketi) */}
            <g id="door-pe-stud" transform="translate(45, 735)">
              <rect x="0" y="0" width="210" height="32" rx="4" fill="#14532d" stroke="#22c55e" strokeWidth="1" />
              <text x="12" y="20" fill="#86efac" fontSize="9" fontWeight="bold">
                ⏚ KAPAK TOPRAKLAMA KORUMASI (PE)
              </text>
              <circle cx="188" cy="16" r="8" fill="#fde047" stroke="#854d0e" strokeWidth="1" />
              <line x1="183" y1="16" x2="193" y2="16" stroke="#713f12" strokeWidth="1.5" />
            </g>
          </g>
        </g>
      )}

      {/* ============================================================ */}
      {/* DURUM 2: KAPAK KAPALI (OPERATÖR ÖN YÜZÜ, PANONUN ÜZERİNDE)   */}
      {/* ============================================================ */}
      {!isOpen && (
        <g id="hinged-door-closed" transform="translate(20, 20)" filter={`url(#doorShadow-${uid})`}>
          {/* 1. Kapak Ön Gövde Paneli (400 x 600 mm Gerçek Endüstriyel Ön Yüz) */}
          <rect
            x="0"
            y="0"
            width="600"
            height="800"
            rx="26"
            ry="26"
            fill={`url(#doorFrontGrad-${uid})`}
            stroke="#94a3b8"
            strokeWidth="2"
          />

          {/* Dış Pah ve Conta Hattı */}
          <rect
            x="8"
            y="8"
            width="584"
            height="784"
            rx="20"
            ry="20"
            fill="none"
            stroke="#ffffff"
            strokeWidth="2"
            strokeOpacity="0.8"
          />
          <rect
            x="14"
            y="14"
            width="572"
            height="772"
            rx="16"
            ry="16"
            fill="none"
            stroke="#cbd5e1"
            strokeWidth="1.2"
          />

          {/* 2. Sağ Çelik Menteşe Başları (Sağa Açılan Pano Menteşeleri) */}
          <g id="closed-door-hinges">
            {/* Üst Menteşe (Y = 110) */}
            <g transform="translate(596, 110)">
              <rect x="0" y="0" width="8" height="28" rx="2" fill="#475569" stroke="#1e293b" strokeWidth="1" />
              <line x1="4" y1="2" x2="4" y2="26" stroke="#94a3b8" strokeWidth="1" />
              <circle cx="4" cy="14" r="2" fill="#1e293b" />
            </g>
            {/* Alt Menteşe (Y = 650) */}
            <g transform="translate(596, 650)">
              <rect x="0" y="0" width="8" height="28" rx="2" fill="#475569" stroke="#1e293b" strokeWidth="1" />
              <line x1="4" y1="2" x2="4" y2="26" stroke="#94a3b8" strokeWidth="1" />
              <circle cx="4" cy="14" r="2" fill="#1e293b" />
            </g>
          </g>

          {/* 3. Çeyrek Dönüşlü Pano Mandalları / Kilitler (Solda) */}
          <g id="door-cam-locks">
            {/* Üst Kilit (Solda: X=35, Y=120) */}
            <g transform="translate(35, 120)">
              <circle cx="0" cy="0" r="14" fill="#334155" stroke="#1e293b" strokeWidth="1.5" />
              <circle cx="0" cy="0" r="10" fill="#94a3b8" stroke="#475569" strokeWidth="1" />
              <rect x="-7" y="-2" width="14" height="4" rx="1" fill="#0f172a" />
            </g>
            {/* Alt Kilit (Solda: X=35, Y=660) */}
            <g transform="translate(35, 660)">
              <circle cx="0" cy="0" r="14" fill="#334155" stroke="#1e293b" strokeWidth="1.5" />
              <circle cx="0" cy="0" r="10" fill="#94a3b8" stroke="#475569" strokeWidth="1" />
              <rect x="-7" y="-2" width="14" height="4" rx="1" fill="#0f172a" />
            </g>
            {/* Ergonomik Gömme Pano Kulpu (Solda: X=23, Y=360) */}
            <g
              transform="translate(23, 360)"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleDoor?.();
              }}
            >
              <rect x="0" y="0" width="22" height="70" rx="5" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5" />
              <rect x="4" y="8" width="14" height="54" rx="3" fill="#0f172a" />
              <title>Kapağı Açmak İçin Tıklayın</title>
            </g>
          </g>

          {/* 4. Kapak Üstü Operatör Çerçevesi & Bilgi Levhası */}
          <g id="door-top-header" transform="translate(50, 45)">
            {/* Pano Ünvan Levhası */}
            <rect x="0" y="0" width="500" height="50" rx="8" fill="#1e293b" stroke="#334155" strokeWidth="1.5" />
            <rect x="4" y="4" width="492" height="42" rx="6" fill="#0f172a" />
            <text x="195" y="24" fill="#38bdf8" fontSize="11.5" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">
              ELEKTRİK KUMANDA PANOSU — OPERATÖR KAPAĞI
            </text>
            <text x="195" y="38" fill="#94a3b8" fontSize="8.5" textAnchor="middle">
              3~ 400V / 230V ASENKRON MOTOR DOĞRUDAN YOL VERME (DOL)
            </text>

            {/* "Kapağı Aç" Butonu */}
            <g
              transform="translate(400, 10)"
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleDoor?.();
              }}
            >
              <rect x="0" y="0" width="85" height="30" rx="5" fill="#0284c7" stroke="#38bdf8" strokeWidth="1" />
              <text x="42.5" y="19" fill="#ffffff" fontSize="10.5" fontWeight="bold" textAnchor="middle">
                📂 Kapağı Aç
              </text>
            </g>
          </g>

          {/* 5. OPERATÖR KUMANDA MASASI (KAPAK ÜZERİNDEKİ BUTONLAR VE LAMBALAR) */}
          <g id="operator-station-front" transform="translate(100, 140)">
            {/* Kumanda Paneli Arka Plakası */}
            <rect
              x="0"
              y="0"
              width="400"
              height="520"
              rx="16"
              ry="16"
              fill="#1e293b"
              stroke="#334155"
              strokeWidth="2"
            />
            <rect
              x="8"
              y="8"
              width="384"
              height="504"
              rx="12"
              ry="12"
              fill="#0f172a"
              stroke="#1e293b"
              strokeWidth="1"
            />

            {/* Sarı Tehlike Levhası */}
            <g transform="translate(50, 24)">
              <rect x="0" y="0" width="300" height="32" rx="4" fill="#ca8a04" stroke="#eab308" strokeWidth="1" />
              <text x="150" y="21" fill="#422006" fontSize="11" fontWeight="bold" fontFamily="sans-serif" textAnchor="middle">
                ⚡ DİKKAT: 400V YÜKSEK GERİLİM ⚡
              </text>
            </g>

            {/* --- 1. SİNYAL LAMBALARI (HL1 YEŞİL & HL2 KIRMIZI) --- */}
            <g id="front-pilot-lamps" transform="translate(0, 90)">
              {/* HL1: ÇALIŞMA LAMBASI (YEŞİL) */}
              {hasHl1 ? (
                <g transform="translate(110, 50)">
                  {/* 22mm Krom Taban Bileziği */}
                  <circle cx="0" cy="0" r="32" fill="#334155" stroke="#64748b" strokeWidth="2" />
                  <circle cx="0" cy="0" r="26" fill="#1e293b" stroke="#0f172a" strokeWidth="1" />

                  {/* Optik Fresnel Lens */}
                  <circle
                    cx="0"
                    cy="0"
                    r="22"
                    fill={lampRunLit ? '#22c55e' : '#14532d'}
                    stroke={lampRunLit ? '#86efac' : '#166534'}
                    strokeWidth="2"
                    filter={lampRunLit ? `url(#greenGlow-${uid})` : undefined}
                  />
                  {/* Lens yüzey prizmaları */}
                  <circle cx="0" cy="0" r="16" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
                  <circle cx="0" cy="0" r="10" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
                  <circle cx="-5" cy="-5" r="6" fill="#ffffff" opacity={lampRunLit ? 0.7 : 0.2} />

                  {/* Etiket Plaketi */}
                  <rect x="-55" y="40" width="110" height="24" rx="3" fill="#1e293b" stroke="#334155" strokeWidth="1" />
                  <text x="0" y="52" fill="#4ade80" fontSize="8.5" fontWeight="bold" textAnchor="middle">HL1 (RUN)</text>
                  <text x="0" y="61" fill="#cbd5e1" fontSize="7" textAnchor="middle">MOTOR ÇALIŞIYOR</text>
                </g>
              ) : (
                <g
                  transform="translate(110, 50)"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceDevice?.('hl1');
                  }}
                >
                  <circle cx="0" cy="0" r="22" fill="#0f172a" stroke="#475569" strokeWidth="1.2" strokeDasharray="3,3" />
                  <text x="0" y="4" fill="#64748b" fontSize="8" fontWeight="bold" textAnchor="middle">HL1</text>
                  <rect x="-45" y="32" width="90" height="18" rx="2" fill="#1e293b" stroke="#334155" strokeWidth="0.8" />
                  <text x="0" y="44" fill="#94a3b8" fontSize="7" textAnchor="middle">HL1 Yuvası (Boş)</text>
                </g>
              )}

              {/* HL2: ARIZA LAMBASI (KIRMIZI) */}
              {hasHl2 ? (
                <g transform="translate(290, 50)">
                  <circle cx="0" cy="0" r="32" fill="#334155" stroke="#64748b" strokeWidth="2" />
                  <circle cx="0" cy="0" r="26" fill="#1e293b" stroke="#0f172a" strokeWidth="1" />

                  <circle
                    cx="0"
                    cy="0"
                    r="22"
                    fill={lampFaultLit ? '#ef4444' : '#7f1d1d'}
                    stroke={lampFaultLit ? '#fca5a5' : '#991b1b'}
                    strokeWidth="2"
                    filter={lampFaultLit ? `url(#redGlow-${uid})` : undefined}
                  />
                  <circle cx="0" cy="0" r="16" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />
                  <circle cx="0" cy="0" r="10" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="0.8" />
                  <circle cx="-5" cy="-5" r="6" fill="#ffffff" opacity={lampFaultLit ? 0.7 : 0.2} />

                  <rect x="-55" y="40" width="110" height="24" rx="3" fill="#1e293b" stroke="#334155" strokeWidth="1" />
                  <text x="0" y="52" fill="#f87171" fontSize="8.5" fontWeight="bold" textAnchor="middle">HL2 (FAULT)</text>
                  <text x="0" y="61" fill="#cbd5e1" fontSize="7" textAnchor="middle">TERMİK ARIZA</text>
                </g>
              ) : (
                <g
                  transform="translate(290, 50)"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceDevice?.('hl2');
                  }}
                >
                  <circle cx="0" cy="0" r="22" fill="#0f172a" stroke="#475569" strokeWidth="1.2" strokeDasharray="3,3" />
                  <text x="0" y="4" fill="#64748b" fontSize="8" fontWeight="bold" textAnchor="middle">HL2</text>
                  <rect x="-45" y="32" width="90" height="18" rx="2" fill="#1e293b" stroke="#334155" strokeWidth="0.8" />
                  <text x="0" y="44" fill="#94a3b8" fontSize="7" textAnchor="middle">HL2 Yuvası (Boş)</text>
                </g>
              )}
            </g>

            {/* --- 2. OPERATÖR BUTONLARI (SB1 STOP & SB2 START) --- */}
            <g id="front-push-buttons" transform="translate(0, 260)">
              {/* SB1: STOP BUTONU (22mm Gömme Kırmızı Buton) */}
              {hasSb1 ? (
                <g
                  id="front-button-stop"
                  transform={`translate(110, 80) ${stopPressed ? 'scale(0.96)' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onStopDown?.();
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                    onStopUp?.();
                  }}
                  onMouseLeave={() => {
                    if (stopPressed) onStopUp?.();
                  }}
                >
                  {/* 22mm Krom Montaj Flanşı */}
                  <circle cx="0" cy="0" r="38" fill="#475569" stroke="#64748b" strokeWidth="2" />
                  <circle cx="0" cy="0" r="32" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5" />

                  {/* Basılabilir Kırmızı Düğme Başlığı */}
                  <circle
                    cx="0"
                    cy="0"
                    r="27"
                    fill={stopPressed ? '#991b1b' : '#dc2626'}
                    stroke="#ef4444"
                    strokeWidth="2"
                  />
                  <circle cx="0" cy="0" r="22" fill="#b91c1c" />
                  <text x="0" y="-3" fill="#ffffff" fontSize="12" fontWeight="bold" textAnchor="middle">STOP</text>
                  <text x="0" y="11" fill="#fca5a5" fontSize="14" fontWeight="bold" textAnchor="middle">O</text>

                  {/* Etiket Plaketi */}
                  <rect x="-55" y="46" width="110" height="24" rx="3" fill="#1e293b" stroke="#334155" strokeWidth="1" />
                  <text x="0" y="58" fill="#f87171" fontSize="8.5" fontWeight="bold" textAnchor="middle">SB1 (STOP)</text>
                  <text x="0" y="67" fill="#cbd5e1" fontSize="7" textAnchor="middle">DEVREYİ KES (NC)</text>
                </g>
              ) : (
                <g
                  transform="translate(110, 80)"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceDevice?.('sb1');
                  }}
                >
                  <circle cx="0" cy="0" r="28" fill="#0f172a" stroke="#ef4444" strokeWidth="1.2" strokeDasharray="3,3" />
                  <text x="0" y="4" fill="#ef4444" fontSize="9" fontWeight="bold" textAnchor="middle">STOP (SB1)</text>
                  <rect x="-45" y="38" width="90" height="18" rx="2" fill="#1e293b" stroke="#334155" strokeWidth="0.8" />
                  <text x="0" y="50" fill="#94a3b8" fontSize="7" textAnchor="middle">SB1 Yuvası (Boş)</text>
                </g>
              )}

              {/* SB2: START BUTONU (22mm Gömme Yeşil Buton) */}
              {hasSb2 ? (
                <g
                  id="front-button-start"
                  transform={`translate(290, 80) ${startPressed ? 'scale(0.96)' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    onStartDown?.();
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                    onStartUp?.();
                  }}
                  onMouseLeave={() => {
                    if (startPressed) onStartUp?.();
                  }}
                >
                  <circle cx="0" cy="0" r="38" fill="#475569" stroke="#64748b" strokeWidth="2" />
                  <circle cx="0" cy="0" r="32" fill="#1e293b" stroke="#0f172a" strokeWidth="1.5" />

                  <circle
                    cx="0"
                    cy="0"
                    r="27"
                    fill={startPressed ? '#14532d' : '#16a34a'}
                    stroke="#22c55e"
                    strokeWidth="2"
                  />
                  <circle cx="0" cy="0" r="22" fill="#15803d" />
                  <text x="0" y="-3" fill="#ffffff" fontSize="12" fontWeight="bold" textAnchor="middle">START</text>
                  <text x="0" y="11" fill="#86efac" fontSize="14" fontWeight="bold" textAnchor="middle">I</text>

                  <rect x="-55" y="46" width="110" height="24" rx="3" fill="#1e293b" stroke="#334155" strokeWidth="1" />
                  <text x="0" y="58" fill="#4ade80" fontSize="8.5" fontWeight="bold" textAnchor="middle">SB2 (START)</text>
                  <text x="0" y="67" fill="#cbd5e1" fontSize="7" textAnchor="middle">MOTORU BAŞLAT (NO)</text>
                </g>
              ) : (
                <g
                  transform="translate(290, 80)"
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPlaceDevice?.('sb2');
                  }}
                >
                  <circle cx="0" cy="0" r="28" fill="#0f172a" stroke="#22c55e" strokeWidth="1.2" strokeDasharray="3,3" />
                  <text x="0" y="4" fill="#22c55e" fontSize="9" fontWeight="bold" textAnchor="middle">START (SB2)</text>
                  <rect x="-45" y="38" width="90" height="18" rx="2" fill="#1e293b" stroke="#334155" strokeWidth="0.8" />
                  <text x="0" y="50" fill="#94a3b8" fontSize="7" textAnchor="middle">SB2 Yuvası (Boş)</text>
                </g>
              )}
            </g>

            {/* Alt Talimat Notu */}
            <g transform="translate(50, 485)">
              <text x="150" y="12" fill="#94a3b8" fontSize="8.5" textAnchor="middle">
                Butonlara doğrudan tıklayarak devreyi çalıştırabilir, kapağı açarak kablolama yapabilirsiniz.
              </text>
            </g>
          </g>
        </g>
      )}
    </g>
  );
};

export default EnclosureDoorSvg;
